<template>
    <page-loading v-if="loading"></page-loading>
    <!-- Outside corporate the group sits in a team settings tab, which already provides the page layout. -->
    <component
        :is="onCorporate() ? VxPageLayout : 'div'"
        v-else
        :layout="!onCorporate() ? undefined : props.groupId ? 'sidebar' : 'create'"
    >
        <page-header v-if="!onCorporate()">
            <span class="inline-flex items-center">
                Details
                <group-status-badge v-if="group.status" :status="group.status" class="ms-3" />
            </span>
        </page-header>

        <template v-if="onCorporate()" v-slot:title>
            <span v-if="!props.groupId" class="inline-flex items-center gap-3">
                <font-awesome-icon widthAuto :icon="faGrid2Plus" class="text-base"></font-awesome-icon>
                <span>New Group</span>
            </span>
            <span v-else class="inline-flex items-center">
                {{ group.name }}
                <group-status-badge v-if="group.status" :status="group.status" class="ms-3" />
            </span>
        </template>

        <template v-if="onCorporate() && !props.groupId" v-slot:description>
            Create a group to connect specific users to a set of resources in Voxie.
        </template>

        <template v-if="props.groupId && onCorporate()" v-slot:subtitle>
            <a :href="`/settings/teams/${teamId}#/groups`"
               class="no-underline! text-sky-600! text-base font-normal">
                <font-awesome-icon widthAuto class="mr-1" :icon="faArrowLeft"></font-awesome-icon>
                All Groups
            </a>
        </template>

        <template v-if="onCorporate() && props.groupId" v-slot:sidebar>
            <page-sidebar appearance="nested" :links="sidebarLinks"></page-sidebar>
        </template>

        <div :class="onCorporate() ? 'w-full' : 'max-w-(--breakpoint-sm)'">
            <vx-alert v-if="group.org_unit && (onCorporate() || (['admin', 'owner'].includes(roleOnCurrentOrgUnit) && currentOrgUnit?.id === group.org_unit.id))" :key="group" class="mb-6" :closeable="false" color="info" shadow :class="{
                'max-w-(--breakpoint-sm) mx-auto': !props.groupId
            }">
                <template v-slot:icon>
                    <font-awesome-icon widthAuto :icon="faSlidersSimple" />
                </template>

                Part of <strong>{{group.org_unit?.name}}</strong>

                <template v-slot:right>
                    <vx-button size="sm" color="info" :href="onCorporate() ? `/settings/teams/${group.team_id}/org-units/${group.org_unit.id}/groups` : `/settings/teams/${group.team_id}#/current-org-unit-groups`">
                        Back to OU
                        <font-awesome-icon widthAuto :icon="faSquareArrowUpLeft"></font-awesome-icon>
                    </vx-button>
                </template>
            </vx-alert>

            <slot></slot>
        </div>
    </component>
</template>

<script setup>
import { faArrowLeft, faGrid2Plus, faSlidersSimple, faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, onMounted, provide } from 'vue';
import { PAGE_HEADING_LEVEL } from '~/provides/pageHeadingLevel';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import PageSidebar from '~/components/general/PageSidebar.vue';
import { useGroupDetails } from './useGroupDetails';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { VxAlert, VxButton, VxPageLayout } from '@voxie/frontend-components';
import { FEATURES } from '~/provides/features';
import GroupStatusBadge from '~/components/general/GroupStatusBadge.vue';

const teamId = Spark.state.currentTeam.id;

const props = defineProps({
    groupId: {
        type: String,
        required: false,
    },
});

const { mount, group, loading } = useGroupDetails();
const { onCorporate, roleOnCurrentOrgUnit, currentOrgUnit } = useCurrentGroup();

// Outside corporate the settings tab owns the layout, so the page-header inside is still the page's h1.
provide(PAGE_HEADING_LEVEL, onCorporate() ? 'h2' : 'h1');

onMounted(() => {
    mount(props.groupId);
});

const sidebarLinks = computed(() => {
    const links = [
        { href: `/settings/teams/${teamId}/groups/${props.groupId}`, label: 'Details' },
        { href: `/settings/teams/${teamId}/groups/${props.groupId}/users`, label: 'Users' },
        { href: `/settings/teams/${teamId}/groups/${props.groupId}/collections`, label: 'Collections' },
        { label: 'Resources', links: resourcesLinks.value },
    ];

    if (group.value?.is_billable) {
        links.push(
            { href: `/settings/teams/${teamId}/groups/${props.groupId}/billing-details`, label: 'Billing Details' },
            { href: `/settings/teams/${teamId}/groups/${props.groupId}/proforma-invoices`, label: 'Upcoming Invoices' },
            { href: `/settings/teams/${teamId}/groups/${props.groupId}/historical-invoices`, label: 'Historical Invoices' },
        );
    }

    return links;
});

const resourcesLinks = computed(() => {
    const links = [
        {
            label: 'Contacts',
            href: `/settings/teams/${teamId}/groups/${props.groupId}/contacts`,
        },
        {
            label: 'Segments',
            href: `/settings/teams/${teamId}/groups/${props.groupId}/segments`,
        },
        {
            label: 'Campaigns',
            href: `/settings/teams/${teamId}/groups/${props.groupId}/campaigns`,
        },
        {
            label: 'Snippets',
            href: `/settings/teams/${teamId}/groups/${props.groupId}/snippets`,
        },
    ];

    if (FEATURES.calendly_integration) {
        links.push({
            label: 'Calendly',
            href: `/settings/teams/${teamId}/groups/${props.groupId}/calendly`,
        });
    }

    if (FEATURES.acuity_integration) {
        links.push({
            label: 'Acuity',
            href: `/settings/teams/${teamId}/groups/${props.groupId}/acuity`,
        });
    }

    return links;
});

</script>
